/**
 * WebSocket client for real-time notifications
 */

import { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../contexts/NotificationsContext';

export interface NotificationMessage {
  type: string;
  payload: any;
  user_id?: string;
}

export interface FileSharedNotification {
  file_id: string;
  file_name: string;
  shared_by: string;
  shared_by_name: string;
  timestamp: number;
}

export interface FileAccessViolationNotification {
  file_id: string;
  file_name: string;
  violated_by: string;
  violated_by_email: string;
  violation_type: string;
  timestamp: number;
}

export interface FileAccessRequestNotification {
  file_id: string;
  file_name: string;
  requested_by: string;
  requested_by_email: string;
  request_reason?: string;
  timestamp: number;
}

export interface NewDeviceNotification {
  device_id: string;
  device_name: string;
  device_type: string;
  registered_at: string;
  ip_address?: string;
  location?: string;
  timestamp: number;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private isConnecting = false;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private connectionHandlers: ((connected: boolean) => void)[] = [];
  private isIntentionallyDisconnected: boolean = false;
  private lastConnectionAttempt: number = 0;
  private connectionCooldown: number = 5000; // 5 seconds cooldown between attempts

  constructor(
    private baseUrl: string,
    private token: string,
    private userId: string,
    private deviceId: string
  ) {}

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now - this.lastConnectionAttempt < this.connectionCooldown) {
      console.log('🕐 Connection cooldown active, skipping connection attempt');
      return;
    }

    this.isConnecting = true;
    this.isIntentionallyDisconnected = false;
    this.lastConnectionAttempt = now;

    try {
      // Use dedicated WebSocket URL if available, otherwise convert HTTP to WS
      const baseWsUrl = process.env.REACT_APP_WS_URL 
        ? `${process.env.REACT_APP_WS_URL}/api/v1/ws`
        : `${this.baseUrl.replace('http', 'ws')}/api/v1/ws`;
      
      // 🔒 WebSocket uses cookies for authentication (httpOnly cookies)
      // No need to pass token in URL - backend reads from cookies
      console.log('🔍 WebSocket Debug - UserID:', this.userId);
      console.log('🔍 WebSocket Debug - DeviceID:', this.deviceId);
      console.log('🔒 WebSocket: Using cookie-based authentication (no token in URL)');
      
      const wsUrl = baseWsUrl; // No token in URL - backend reads from cookies
      console.log('🔌 Connecting to WebSocket:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('✅ WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.notifyConnectionHandlers(true);
      };

      this.ws.onmessage = (event) => {
        try {
          const message: NotificationMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.notifyConnectionHandlers(false);
        
        if (event.code !== 1000) { // Not a normal closure
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.isConnecting = false;
        this.notifyConnectionHandlers(false);
      };

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyDisconnected = true;
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Register a message handler for a specific notification type
   */
  onMessage(type: string, handler: (data: any) => void): void {
    this.messageHandlers.set(type, handler);
  }

  /**
   * Register a connection status handler
   */
  onConnectionChange(handler: (connected: boolean) => void): void {
    this.connectionHandlers.push(handler);
  }

  /**
   * Send a message to the server
   */
  send(message: any): void {
    if (this.isConnected()) {
      this.ws!.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message');
    }
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(message: NotificationMessage): void {
    console.log('📨 Received notification:', message.type, message.payload);

    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message.payload);
    } else {
      console.log('⚠️ No handler for message type:', message.type);
    }
  }

  /**
   * Notify connection handlers
   */
  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => handler(connected));
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.isIntentionallyDisconnected) {
      console.log('🛑 Skipping reconnection - intentionally disconnected');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, this.maxReconnectDelay);
    
    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }
}

/**
 * WebSocket hook for React components
 */
export function useWebSocket(
  baseUrl: string,
  token: string,
  userId: string,
  deviceId: string
) {
  const [wsClient, setWsClient] = useState<WebSocketClient | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { addNotification, updateNotification } = useNotifications();
  const addNotificationRef = useRef(addNotification);
  const updateNotificationRef = useRef(updateNotification);
  addNotificationRef.current = addNotification;
  updateNotificationRef.current = updateNotification;

  useEffect(() => {
    const client = new WebSocketClient(baseUrl, token, userId, deviceId);
    
    // Set up connection handler
    client.onConnectionChange((connected) => {
      setIsConnected(connected);
    });

    // Set up notification handlers (use refs so effect doesn't re-run on every notification state change -> avoids reconnect loop and memory growth)
    // Note: Backend sends notifications with structure: { notification_id, title, message, payload, created_at, ... }
    client.onMessage('file_shared', (data: any) => {
      const title = data.title || 'Asset Shared'; // Generic title that covers both files and folders
      const message = data.message || (data.payload?.file_name 
        ? `${data.payload.file_name} has been shared with you by ${data.payload.shared_by_name || 'Unknown user'}`
        : 'An asset has been shared with you');
      
      // Ensure notification_id is in payload for ID extraction
      const payload = {
        ...(data.payload || data),
        notification_id: data.notification_id || data.id || data.payload?.notification_id,
        id: data.notification_id || data.id || data.payload?.id,
      };
      
      addNotificationRef.current({
        type: 'file_shared',
        title,
        message,
        payload,
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000,
      });
    });

    client.onMessage('folder_shared', (data: any) => {
      const title = data.title || 'Asset Shared'; // Generic title that covers both files and folders
      const message = data.message || (data.payload?.file_name 
        ? `${data.payload.file_name} has been shared with you by ${data.payload.shared_by_name || 'Unknown user'}`
        : 'An asset has been shared with you');
      
      // Ensure notification_id is in payload for ID extraction
      const payload = {
        ...(data.payload || data),
        notification_id: data.notification_id || data.id || data.payload?.notification_id,
        id: data.notification_id || data.id || data.payload?.id,
      };
      
      addNotificationRef.current({
        type: 'folder_shared',
        title,
        message,
        payload,
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000,
      });
    });

    // Access violation notifications
    client.onMessage('file_access_violation', (data: any) => {
      // Backend sends: { notification_id, title, message, payload: {...}, created_at, ... }
      // notification_id is included in the payload for deduplication in addNotification
      const title = data.title || 'Access Violation';
      const message = data.message || (data.payload?.violated_by_email 
        ? `${data.payload.violated_by_email} attempted to access your file ${data.payload.file_name} but was denied`
        : 'File access violation detected');
      
      addNotificationRef.current({
        type: 'file_access_violation',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000
      });
    });

    // File access request notifications (for file owners)
    client.onMessage('file_access_request', (data: any) => {
      const title = data.title || 'Access Request';
      const message = data.message || (data.payload?.requested_by_email
        ? `${data.payload.requested_by_email} is requesting access to your file ${data.payload.file_name}`
        : 'File access request received');
      
      addNotificationRef.current({
        type: 'file_access_request',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || data.timestamp || Date.now() / 1000
      });
    });

    // Access count limit request notifications
    client.onMessage('access_count_limit_request', (data: any) => {
      const title = data.title || 'Access Request';
      const message = data.message || (data.payload?.requester_email
        ? `${data.payload.requester_email} requested access limit change for file: ${data.payload.file_name}`
        : 'Access limit change requested');
      
      addNotificationRef.current({
        type: 'access_count_limit_request',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || data.timestamp || Date.now() / 1000
      });
    });

    // Access period limit request notifications
    client.onMessage('access_period_limit_request', (data: any) => {
      const title = data.title || 'Access Request';
      const message = data.message || (data.payload?.requester_email
        ? `${data.payload.requester_email} requested access period change for file: ${data.payload.file_name}`
        : 'Access period change requested');
      
      addNotificationRef.current({
        type: 'access_period_limit_request',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || data.timestamp || Date.now() / 1000
      });
    });

    // Asset permissions request notifications
    client.onMessage('asset_permissions_request', (data: any) => {
      const title = data.title || 'Access Request';
      const message = data.message || (data.payload?.requester_email
        ? `${data.payload.requester_email} requested permission change for file: ${data.payload.file_name}`
        : 'Asset permissions change requested');
      
      addNotificationRef.current({
        type: 'asset_permissions_request',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || data.timestamp || Date.now() / 1000
      });
    });

    // New device registration notifications
    client.onMessage('new_device_registered', (data: any) => {
      const title = data.title || 'New Device Added';
      // Handle nested payload structure: data.payload.payload contains the actual device data
      const deviceData = data.payload || data;
      const deviceName = deviceData.device_name || deviceData.payload?.device_name;
      const deviceType = deviceData.device_type || deviceData.payload?.device_type;
      const message = data.message || (deviceName
        ? `A new ${deviceType || 'device'} device "${deviceName}" was added to your account`
        : 'A new device was added to your account');
      
      addNotificationRef.current({
        type: 'new_device_registered',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000
      });
    });
    
    // Infrastructure provisioning notifications
    client.onMessage('infrastructure_provisioning', (data: any) => {
      const title = data.title || 'Infrastructure Provisioning Started';
      const message = data.message || 'Your Infisical instance is being provisioned. You\'ll receive a notification when it\'s ready.';
      
      addNotificationRef.current({
        type: 'infrastructure_provisioning',
        title: title,
        message: message,
        payload: data,
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000
      });
    });

    // Infrastructure ready notifications
    client.onMessage('infrastructure_ready', (data: any) => {
      const title = data.title || 'Infrastructure Ready';
      const message = data.message || (data.payload?.url 
        ? `Your Infisical instance is ready at ${data.payload.url}. Click to view details.`
        : 'Your Infisical instance is ready. Click to view details.');
      
      addNotificationRef.current({
        type: 'infrastructure_ready',
        title: title,
        message: message,
        payload: data,
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000
      });
    });

    // Access request response notifications
    client.onMessage('access_request_responded', (data: any) => {
      const title = data.title || 'Access Request Response';
      const message = data.message || 'Your access request has been responded to.';
      
      addNotificationRef.current({
        type: 'access_request_responded',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || data.timestamp || Date.now() / 1000
      });
    });

    // Access request canceled notifications
    client.onMessage('access_request_canceled', (data: any) => {
      const title = data.title || 'Access Request Canceled';
      const message = data.message || 'An access request has been canceled.';
      
      addNotificationRef.current({
        type: 'access_request_canceled',
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || data.timestamp || Date.now() / 1000
      });
    });
    
    // Generic notification handler (for any notification type from backend)
    client.onMessage('notification', (data: any) => {
      // Backend might send notifications with type "notification" as fallback
      const notificationType = data.type || 'notification';
      const title = data.title || 'Notification';
      const message = data.message || 'You have a new notification';
      
      addNotificationRef.current({
        type: notificationType,
        title: title,
        message: message,
        payload: data, // Full payload includes notification_id for deduplication
        user_id: userId,
        timestamp: data.created_at || Date.now() / 1000
      });
    });

    // Handle notification updates (for infrastructure provisioning status changes)
    client.onMessage('notification_updated', (data: any) => {
      const notificationId = data.notification_id || data.id;
      if (notificationId) {
        updateNotificationRef.current(notificationId, {
          title: data.title,
          message: data.message,
          payload: data.payload || data,
          timestamp: data.timestamp || Date.now() / 1000,
        });
      }
    });
    
    // Handle read status updates from other clients
    // Note: We can't use hooks here, so we'll handle this in the NotificationCenter component
    // by listening to WebSocket messages and updating the context


    setWsClient(client);
    client.connect();

    return () => {
      client.disconnect();
    };
  }, [baseUrl, token, userId, deviceId]);

  return {
    wsClient,
    isConnected
  };
}
